import styled, { css } from 'styled-components'
import { tint } from 'polished'

import { mediaMax } from 'styles/media'

type WrapperProps = {
  $daySize?: number
  $monthsGap?: string
}

/* Inline wrapper that horizontally flips its child SVG. Used by the prev
   nav button so we can reuse `ArrowRightSlim` (there's no matching
   ArrowLeftSlim icon) without forking a new icon component. */
export const FlippedChevronWrap = styled.span`
  display: inline-flex;
  transform: scaleX(-1);
`

/* react-day-picker's default stylesheet (loaded via clientRegistration)
   declares its CSS variables on `.rdp-root`, so a same-named declaration
   on a parent element doesn't cascade through. Overrides have to target
   `.rdp-root` (or an inside element) and rely on source order — styled
   components are injected after rdp's stylesheet, so our `.rdp-root`
   rule wins. */
export const Wrapper = styled.div<WrapperProps>`
  /* Center the rdp grid below md so the single-month layout sits in the
     middle of the wrapper instead of hugging the left edge (e.g. the
     mobile search-filter modal). On md+ surfaces — booking calendar
     and the desktop search filter — the wrapper handles its own
     horizontal layout. */
  ${mediaMax.md`
    display: flex;
    flex-direction: column;
    align-items: center;
  `}

  .rdp-root {
    color: ${({ theme }) => theme['-ds-neutral-700'].value};

    --rdp-accent-color: ${({ theme }) => theme['-ds-core-400'].value};
    --rdp-accent-background-color: ${({ theme }) =>
      tint(0.8, theme['-ds-core-400'].value)};
    --rdp-range_middle-background-color: ${({ theme }) =>
      tint(0.7, theme['-ds-core-400'].value)};
    --rdp-range_middle-color: inherit;
    --rdp-range_start-date-background-color: ${({ theme }) =>
      theme['-ds-core-400'].value};
    --rdp-range_end-date-background-color: ${({ theme }) =>
      theme['-ds-core-400'].value};
    --rdp-today-color: ${({ theme }) => theme['-ds-core-400'].value};
    --rdp-day_button-border-radius: 0;
    --rdp-day_button-border: 1px solid transparent;
    --rdp-nav_button-height: 3.5rem;
    --rdp-nav_button-width: 4.25rem;
    --rdp-nav-height: 3.3rem;

    ${({ $daySize }) =>
      $daySize &&
      css`
        --rdp-day-width: ${$daySize}px;
        --rdp-day-height: ${$daySize}px;
        --rdp-day_button-width: ${$daySize}px;
        --rdp-day_button-height: ${$daySize}px;
      `}

    ${({ $monthsGap }) =>
      $monthsGap &&
      css`
        --rdp-months-gap: ${$monthsGap};
      `}
  }

  .rdp-month_caption {
    font-weight: 500;
    justify-content: center;
  }

  /* Breathe the calendar grid away from the prev/next nav + month
     caption row above it. Applied at the shared wrapper so every
     surface (search filters, booking enquiry, host availability,
     story offer, creator availability) gets the same gap by default. */
  .rdp-month_grid {
    margin-top: 10px;
  }

  .rdp-weekday {
    font-weight: normal;
    color: ${({ theme }) => theme['-ds-neutral-500'].value};
  }

  .rdp-day_button {
    font-size: 14px;
  }

  .rdp-selected {
    font-weight: normal;
  }

  .rdp-day_button:not([aria-disabled='true']):hover {
    background: ${({ theme }) => tint(0.8, theme['-ds-core-400'].value)};
    border-color: ${({ theme }) => tint(0.7, theme['-ds-core-400'].value)};
  }

  /* Selected non-middle cells (partial-range start, complete range_start,
     range_end) get the solid accent fill + white text. rdp v9 only sets
     the range_start / range_end modifiers when both endpoints are picked,
     so we target .rdp-selected:not(.rdp-range_middle) to cover the
     partial-selection case too. */
  .rdp-selected:not(.rdp-range_middle) .rdp-day_button {
    background: var(--rdp-range_start-date-background-color);
    color: #fff;
  }

  .rdp-selected:not(.rdp-range_middle) .rdp-day_button:hover {
    background: var(--rdp-range_start-date-background-color);
    border-color: var(--rdp-range_start-date-background-color);
  }

  /* Hover-preview range: every cell between the selected start and the
     currently hovered cell is marked .rdp-preview_middle by the wrapper's
     custom modifier. Paint them with the same range_middle background as
     a fully-selected range so the user can preview the span before
     committing the end date. */
  .rdp-preview_middle .rdp-day_button {
    background: var(--rdp-range_middle-background-color);
  }

  .rdp-day_button[aria-disabled='true'] {
    cursor: default;
  }

  /* Month navigation — a boxed icon button sitting at the outer edge of
     each calendar surface so the prev/next affordance is consistent
     across the booking, search-filter, host, and story-offer calendars.
     rdp's default chevron renders unboxed, which feels lightweight next
     to a tappable day grid. Size comes from the --rdp-nav_button-*
     variables overridden on .rdp-root above. */
  .rdp-button_previous,
  .rdp-button_next {
    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme['-ds-neutral-300'].value};
    color: ${({ theme }) => theme['-ds-neutral-700'].value};
  }

  .rdp-button_previous:not(:disabled):not([aria-disabled='true']):hover,
  .rdp-button_next:not(:disabled):not([aria-disabled='true']):hover {
    border-color: ${({ theme }) => theme['-ds-neutral-500'].value};
    background: ${({ theme }) => theme['-ds-neutral-100'].value};
  }

  .rdp-chevron {
    fill: currentColor;
  }

  /* ArrowRightSlim has a 1000×1000 viewBox with a horizontal arrow that
     fills most of the canvas. Sizing it to the icon-button standard
     16px keeps the arrow legible inside the nav button regardless of
     the button's outer dimensions. */
  .rdp-button_previous svg,
  .rdp-button_next svg {
    width: 16px;
    height: 16px;
  }
`
